import React from 'react';
import { ShieldAlert, Sparkles, Mail } from 'lucide-react';

/**
 * Feb 2026 v7 — System Lockdown maintenance screen.
 *
 * Rendered by <App /> whenever `/api/lockdown/status` reports
 * `enabled: true` AND the current viewer is not a super admin.
 *
 * Design intent: distinctive from generic "site down" pages. Deep-navy
 * gradient with an animated aurora blob, a soft grid overlay, a
 * shield-alert monogram and a headline + custom message pulled from
 * settings. Fully responsive down to 320 px.
 */
const LockdownScreen = ({
  title = 'We\u2019re getting things back on track',
  message = 'Our team is fine-tuning the platform to serve you better. The store will be back online in a few minutes.',
  contact = '',
}) => {
  return (
    <div
      className="fixed inset-0 z-[9999] overflow-hidden bg-[#050B1F] text-white antialiased"
      data-testid="lockdown-screen"
    >
      {/* Animated aurora blobs — CSS keyframes registered below */}
      <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full opacity-70 blur-3xl lockdown-blob-a" />
      <div className="absolute -bottom-40 -right-32 w-[560px] h-[560px] rounded-full opacity-60 blur-3xl lockdown-blob-b" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[300px] h-[300px] rounded-full opacity-40 blur-3xl lockdown-blob-c" />

      {/* Soft grid overlay */}
      <svg
        aria-hidden="true"
        className="absolute inset-0 w-full h-full opacity-[0.08]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id="lockdown-grid" width="42" height="42" patternUnits="userSpaceOnUse">
            <path d="M42 0H0V42" fill="none" stroke="#fff" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#lockdown-grid)" />
      </svg>

      {/* Content */}
      <div className="relative h-full w-full flex items-center justify-center px-6">
        <div className="max-w-2xl w-full text-center">
          {/* Monogram */}
          <div className="flex justify-center mb-6">
            <div className="relative inline-flex items-center justify-center w-24 h-24">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-cyan-400/30 to-fuchsia-500/30 blur-xl lockdown-pulse" />
              <div className="relative w-24 h-24 rounded-3xl bg-white/5 border border-white/20 backdrop-blur-lg flex items-center justify-center shadow-2xl shadow-cyan-500/20">
                <ShieldAlert className="w-11 h-11 text-cyan-300 drop-shadow-[0_0_10px_rgba(103,232,249,0.6)]" strokeWidth={1.5} />
                <Sparkles className="absolute -top-2 -right-2 w-5 h-5 text-fuchsia-300 lockdown-twinkle" />
              </div>
            </div>
          </div>

          {/* Kicker */}
          <div className="flex justify-center mb-6">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-bold tracking-[0.24em] uppercase text-cyan-200"
              data-testid="lockdown-kicker"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 lockdown-pulse-dot" />
              System Maintenance
            </div>
          </div>

          {/* Title */}
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-4 bg-gradient-to-r from-white via-cyan-100 to-fuchsia-200 bg-clip-text text-transparent"
            data-testid="lockdown-title"
          >
            {title}
          </h1>

          {/* Message */}
          <p
            className="text-base sm:text-lg text-white/70 leading-relaxed max-w-xl mx-auto mb-8"
            data-testid="lockdown-message"
          >
            {message}
          </p>

          {/* Optional contact */}
          {contact ? (
            <a
              href={contact.includes('@') ? `mailto:${contact}` : contact}
              className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 px-5 py-2.5 text-sm font-semibold text-white transition backdrop-blur"
              data-testid="lockdown-contact-btn"
            >
              <Mail className="w-4 h-4" /> Reach out
            </a>
          ) : null}

          {/* Footer brand */}
          <div className="mt-14 text-[11px] tracking-[0.28em] uppercase font-bold text-white/40">
            Mohammed Riaz &amp; Partner LLC
          </div>
        </div>
      </div>

      {/* Scoped animations — no Tailwind config edits needed */}
      <style>{`
        @keyframes lockdown-drift-a {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%      { transform: translate(80px, 60px) scale(1.15); }
        }
        @keyframes lockdown-drift-b {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%      { transform: translate(-80px, -60px) scale(1.2); }
        }
        @keyframes lockdown-drift-c {
          0%, 100% { transform: translate(-50%, 0) scale(1); opacity: 0.4; }
          50%      { transform: translate(-40%, 20px) scale(1.1); opacity: 0.55; }
        }
        @keyframes lockdown-pulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50%      { opacity: 1;   transform: scale(1.08); }
        }
        @keyframes lockdown-twinkle {
          0%, 100% { opacity: 1;   transform: scale(1)   rotate(0deg); }
          50%      { opacity: 0.4; transform: scale(0.7) rotate(20deg); }
        }
        @keyframes lockdown-pulse-dot {
          0%, 100% { box-shadow: 0 0 0 0 rgba(103,232,249, 0.7); }
          50%      { box-shadow: 0 0 0 8px rgba(103,232,249, 0);   }
        }
        .lockdown-blob-a { background: radial-gradient(closest-side, #22d3ee 0%, transparent 70%); animation: lockdown-drift-a 14s ease-in-out infinite; }
        .lockdown-blob-b { background: radial-gradient(closest-side, #a855f7 0%, transparent 70%); animation: lockdown-drift-b 18s ease-in-out infinite; }
        .lockdown-blob-c { background: radial-gradient(closest-side, #f472b6 0%, transparent 70%); animation: lockdown-drift-c 20s ease-in-out infinite; }
        .lockdown-pulse   { animation: lockdown-pulse 3.6s ease-in-out infinite; }
        .lockdown-twinkle { animation: lockdown-twinkle 2.8s ease-in-out infinite; }
        .lockdown-pulse-dot { animation: lockdown-pulse-dot 2s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default LockdownScreen;
